import React, {useEffect, useState} from "react";
import {Link, LoaderFunctionArgs, useLoaderData, useNavigate} from "react-router-dom";
import axios from "axios";
import {useSession} from "./contexts/SessionContext";
import {DataGrid, GridColDef, GridToolbarContainer, GridToolbarQuickFilter} from "@mui/x-data-grid";
import {NPC, Plot, PlotEvent, PlotTag} from "../../server/Plot";
import {Monster, MonsterType} from "../../server/monster";
import {
    Autocomplete,
    Button,
    ButtonGroup,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    TextField,
    Tooltip
} from "@mui/material";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EditIcon from '@mui/icons-material/Edit';
import Container from "@mui/material/Container";
import VerifiedIcon from '@mui/icons-material/Verified';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PostAddIcon from '@mui/icons-material/PostAdd';
import SaveIcon from '@mui/icons-material/Save';
import PhishingIcon from '@mui/icons-material/Phishing';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import PrintIcon from '@mui/icons-material/Print';
import {PlotPrint} from "./components/PlotPrint";
import {NPCList} from "./components/NPCList";
import {FaceList} from "./components/FaceList";
import {EventLanding} from "./EventViewer";
import {useTimezone} from "./contexts/TimezoneContext";
import {utcToLocalInput, localInputToUtc} from "./utils/timezone";

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const columns: GridColDef[] = [
    {
        field: 'name', headerName: 'Plot', headerAlign: "center", renderCell: (params) => (
            <Link to={'./' + params.row.id}>{params.value}</Link>
        ), minWidth: 300
    },
    {field: 'short_desc', headerName: 'Short Description', headerAlign: "center", minWidth: 400},
    {field: 'status', headerName: 'Status', headerAlign: "center", align: "center"},
    {field: 'writer', headerName: 'Writer', headerAlign: "center"},
    {field: 'updated_at', headerName: 'Last Update', headerAlign: "center"},
    {
        field: 'hook', headerName: '', width: 48, sortable: false, align: 'center',
        renderCell: (params) => {
            if (!!params.row.hookId) return null;
            return (
                <Tooltip title="No hook assigned">
                    <Box sx={{position: 'relative', width: 24, height: 24}}>
                        <PhishingIcon sx={{position: 'absolute', color: 'error.main', fontSize: 22, p:.1}}/>
                        <DoNotDisturbIcon sx={{position: 'absolute', color: 'error.main', fontSize: 24}}/>
                    </Box>
                </Tooltip>
            );
        }
    },
]

export const plotListLoader = async ({params}: LoaderFunctionArgs): Promise<Plot | null> => {
    return loadPlotList();
}

export async function loadPlotList(): Promise<any> {
    const apiEndpoint = "/plot/plots"
    return await axios.get(apiEndpoint).then(({data}) => {
        return data;
    })
}

export const plotLoader = async ({params}: LoaderFunctionArgs): Promise<Plot | null> => {
    if (typeof params.id !== "undefined") {
        return loadPlotForId(parseInt(params.id));
    } else {
        return null
    }
}

export async function loadPlotForId(id: number): Promise<any> {
    const apiEndpoint = "/plot/plots/" + id
    return await axios.get(apiEndpoint).then(({data}) => {
        return data;
    })
}

export function PlotList() {
    const [plots, setReg] = useState(useLoaderData() as Plot[]);
    const [newPlotOpen, setNewPlotOpen] = React.useState(false);
    const [plotNameError, setPlotNameError] = React.useState<string | null>(null);
    const handleOpenNewPlot = () => setNewPlotOpen(true);
    const handleCloseNewPlot = () => {
        setNewPlotOpen(false);
        setPlotNameError(null);
    };

    let newPlotName = ""

    async function createNewPlot() {
        try {
            const {data} = await axios.post("/plot/plots/", {name: newPlotName})
            newPlotName = ""
            setReg(prev => [...prev, data])
            handleCloseNewPlot()
        } catch (err: any) {
            if (err?.response?.status === 409) {
                setPlotNameError(err.response.data?.error ?? 'A plot with this name already exists.')
            }
        }
    }

    function QuickFilterToolBar() {
        return (
            <GridToolbarContainer>
                <GridToolbarQuickFilter/>
                <Button onClick={handleOpenNewPlot}><PostAddIcon/></Button>
            </GridToolbarContainer>
        )
    }

    return <React.Fragment>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" component="h4" align="center">Plots</Typography>

                    <DataGrid slots={{toolbar: QuickFilterToolBar}}
                              autoHeight={true}
                              rows={plots}
                              columns={columns}
                              getRowId={(row) => row.id}
                              density={"compact"}
                              initialState={{
                                  pagination: {
                                      paginationModel: {
                                          pageSize: 24,
                                      },
                                  },
                              }}
                    />
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <EventLanding/>
            </Grid>
            <Grid item xs={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    {NPCList()}
                </Paper>
            </Grid>
            <Grid item xs={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    {FaceList()}
                </Paper>
            </Grid>

        </Grid>


        <Modal
            open={newPlotOpen}
            onClose={handleCloseNewPlot}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={modalStyle}>
                <Typography align={"center"} id="modal-modal-title" variant="h5" component="h4">
                    Creating New Plot
                </Typography>
                <TextField fullWidth id='plotName' label='New Plot Name' name="newPlotName"
                           error={!!plotNameError}
                           helperText={plotNameError ?? ''}
                           onChange={(ev) => {
                               newPlotName = ev.target.value
                               const dupe = plots.some(p => p.name.toLowerCase() === ev.target.value.toLowerCase())
                               setPlotNameError(dupe ? 'A plot with this name already exists.' : null)
                           }}/>
                <Container sx={{textAlign: 'center'}}><Button onClick={createNewPlot}
                                                              disabled={!!plotNameError}>Submit</Button></Container>
            </Box>
        </Modal>
    </React.Fragment>

}

export interface npc {
    label: string,
    id
        :
        number
}

export function PlotViewer(params: any) {
    const [plot, setPlot] = useState(useLoaderData() as Plot);
    const [editable, setEditable] = useState(false);
    const navigate = useNavigate();
    const tz = useTimezone();
    const [hookModalOpen, setHookModalOpen] = React.useState(false);
    const [createHookModalOpen, setCreateHookModalOpen] = React.useState(false);
    const [npcModalOpen, setNPCModalOpen] = React.useState(false);
    const [monsterModalOpen, setMonsterModalOpen] = React.useState(false);
    const [npcs, setNPCs] = React.useState(new Array<NPC>())
    const [monsterTypes, setMonsterTypes] = React.useState(new Array<MonsterType>())
    const [selectedMonsterType, setSelectedMonsterType] = React.useState<MonsterType | null>(null)
    const [monsterQty, setMonsterQty] = React.useState(1)
    const {roles: userRoles, loading: sessionLoading} = useSession()
    const [newHookName, setNewHookName] = React.useState('')
    const [newHookPersonality, setNewHookPersonality] = React.useState('')
    const [newHookHistory, setNewHookHistory] = React.useState('')
    const [createNPCModalOpen, setCreateNPCModalOpen] = React.useState(false)
    const [newNPCName, setNewNPCName] = React.useState('')
    const [newNPCPersonality, setNewNPCPersonality] = React.useState('')
    const [newNPCHistory, setNewNPCHistory] = React.useState('')
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false)
    const [tags, setTags] = React.useState<PlotTag[]>(plot?.tags ?? [])
    const [newTagName, setNewTagName] = React.useState('')
    const [allTagNames, setAllTagNames] = React.useState<string[]>([])
    const [writers, setWriters] = React.useState<string[]>([])
    const [leads, setLeads] = React.useState<string[]>([])
    const [plotTypes, setPlotTypes] = React.useState<string[]>([])
    const [eventModalOpen, setEventModalOpen] = React.useState(false)
    const [events, setEvents] = React.useState<PlotEvent[]>([])
    const [selectedEvent, setSelectedEvent] = React.useState<PlotEvent | null>(null)
    const [plotStartTime, setPlotStartTime] = React.useState('')
    const [assignedMonsterModalOpen, setAssignedMonsterModalOpen] = React.useState(false)
    const [allMonsters, setAllMonsters] = React.useState<Monster[]>([])
    const [selectedAssignedMonster, setSelectedAssignedMonster] = React.useState<Monster | null>(null)
    const [assignedMonsterQty, setAssignedMonsterQty] = React.useState(1)
    const [assignedMonsterError, setAssignedMonsterError] = React.useState<string | null>(null)

    useEffect(() => {
        if (sessionLoading) return
        axios.get("/plot/npcs").then(response => {
            setNPCs(response.data)
        }).catch(() => {
        })
        if (userRoles.includes('MonsterManager')) {
            axios.get("/monsters/type").then(response => {
                setMonsterTypes(response.data)
            }).catch(() => {
            })
        }
        axios.get("/plot/events").then(({data}) => {
            setEvents(data)
        }).catch(() => {
        })
        axios.get("/plot/tags").then(({data}) => {
            setAllTagNames(data)
        }).catch(() => {
        })
        axios.get("/plot/writers").then(({data}) => {
            setWriters(data)
        }).catch(() => {
        })
        axios.get("/plot/leads").then(({data}) => {
            setLeads(data)
        }).catch(() => {
        })
        axios.get("/plot/types").then(({data}) => {
            setPlotTypes(data)
        }).catch(() => {
        })
    }, [sessionLoading])

    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if (e.ctrlKey && e.key === 'e') {
                e.preventDefault();
                toggleEdit()
            } else if (e.ctrlKey && e.key === 's') {
                e.preventDefault();
                if (editable) savePlot()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [editable])

    const handleOpenHookModal = () => setHookModalOpen(true)
    const handleCloseHookModal = () => setHookModalOpen(false)

    const handleOpenNPCModal = () => setNPCModalOpen(true)
    const handleCloseNPCModal = () => setNPCModalOpen(false)

    const handleOpenMonsterModal = () => setMonsterModalOpen(true)
    const handleCloseMonsterModal = () => {
        setMonsterModalOpen(false)
        setSelectedMonsterType(null)
        setMonsterQty(1)
    }

    function addMonsterToPlot() {
        if (!selectedMonsterType) return
        const entry = {type: selectedMonsterType, quantity: monsterQty}
        const existing = plot.plotCharacters?.monsters_requested ?? []
        setPlot({
            ...plot,
            plotCharacters: {
                ...plot.plotCharacters,
                monsters_requested: [...existing, entry]
            }
        })
        handleCloseMonsterModal()
    }

    function updateMonsterQty(typeId: number, delta: number) {
        setPlot({
            ...plot,
            plotCharacters: {
                ...plot.plotCharacters,
                monsters_requested: plot.plotCharacters?.monsters_requested?.map(m =>
                    m.type.id === typeId
                        ? {...m, quantity: Math.max(1, m.quantity + delta)}
                        : m
                ) ?? []
            }
        })
    }

    function removeMonsterFromPlot(typeId: number) {
        setPlot({
            ...plot,
            plotCharacters: {
                ...plot.plotCharacters,
                monsters_requested: plot.plotCharacters?.monsters_requested?.filter(m => m.type.id !== typeId) ?? []
            }
        })
    }

    function monsterTypeOptions() {
        return monsterTypes.map(mt => ({
            label: `${mt.species?.name} — ${mt.name}`,
            id: mt.id as number,
            type: mt
        }))
    }

    function toggleEdit() {
        setEditable(!editable)
    }

    async function addTag() {
        const trimmed = newTagName.trim()
        if (!trimmed) return
        if (tags.some(t => t.name.toLowerCase() === trimmed.toLowerCase())) return
        await axios.post(`/plot/plots/${plot.id}/tags`, {name: trimmed}).then(({data}) => {
            setTags(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)))
            setAllTagNames(prev => prev.includes(data.name) ? prev : [...prev, data.name].sort())
            setNewTagName('')
        })
    }

    async function removeTag(id: number) {
        await axios.delete(`/plot/tags/${id}`).then(() => {
            setTags(prev => prev.filter(t => t.id !== id))
        })
    }

    function savePlot() {
        const apiEndpoint = "/plot/plots/" + plot.id
        return axios.put(apiEndpoint, plot).then(({data}) => {
            setPlot(data)
            setEditable(false)
            return data;
        })
    }

    async function submitForReview() {
        const updated = {...plot, status: 'REVIEW'}
        await axios.put(`/plot/plots/${plot.id}`, updated).then(({data}) => {
            setPlot(data)
        })
    }

    async function acceptPlot() {
        await axios.put(`/plot/plots/${plot.id}/accept`).then(({data}) => {
            setPlot(data)
        })
    }

    async function rejectPlot() {
        await axios.put(`/plot/plots/${plot.id}/reject`).then(({data}) => {
            setPlot(data)
        })
    }

    async function reopenPlot() {
        await axios.put(`/plot/plots/${plot.id}/reopen`).then(({data}) => {
            setPlot(data)
        })
    }

    async function assignToEvent() {
        if (!selectedEvent) return
        await axios.put(`/plot/plots/${plot.id}/run`, {
            eventId: selectedEvent.id,
            startTime: plotStartTime ? localInputToUtc(plotStartTime, tz) : null
        }).then(({data}) => {
            setPlot(data)
            setEventModalOpen(false)
            setSelectedEvent(null)
            setPlotStartTime('')
        })
    }

    async function completePlot() {
        await axios.put(`/plot/plots/${plot.id}/complete`, {
            story_who: plot.story_who,
            story_outcome: plot.story_outcome,
            story_followup: plot.story_followup,
        }).then(({data}) => {
            setPlot(data)
        })
    }

    async function confirmDelete() {
        await axios.delete(`/plot/plots/${plot.id}`).then(() => {
            navigate('/plot')
        })
    }

    const canReview = userRoles.includes('GM') || userRoles.includes('Owner')

    function peopleNeeded(): number {
        const hook = (plot.hook) ? 1 : 0
        const npcs = (plot as any).additional_characters?.additional_NPCS?.length
            ?? plot.plotCharacters?.additional_npcs?.length
            ?? 0
        const serverMonsters = ((plot as any).additional_characters?.requested_monsters ?? [])
            .reduce((s: number, m: any) => s + (m.quantity ?? 0), 0)
        const clientMonsters = (plot.plotCharacters?.monsters_requested ?? [])
            .reduce((s: number, m: any) => s + (m.quantity ?? 0), 0)
        const dedicatedMarshals = (plot.plotCharacters?.namedMonstersRequested ?? [])
            .filter(m => (m.monster as any).dedicated_marshal).length
        return hook + npcs + (serverMonsters || clientMonsters) + dedicatedMarshals
    }

    function nPCAutoComplete() {
        let ac = new Array<{ label: string, id: number }>()
        npcs.forEach((npc) => {
            ac.push({label: npc.name, id: npc.id as number})
        })
        return ac
    }


    function addHookToPlot(id: number) {
        function checkId(npc: NPC) {
            if (npc.id == id) {
                return npc as NPC
            }
        }

        plot.hook = npcs.find(checkId) as NPC
    }

    async function createAndAddNPC() {
        if (!newNPCName.trim()) return
        const npc = {
            name: newNPCName,
            type: 'NPC',
            extended: {personality: newNPCPersonality, history: newNPCHistory}
        }
        await axios.post('/plot/npcs/', npc).then(({data}) => {
            setNPCs(prev => [...prev, data as NPC])
            const existing = plot.plotCharacters?.additional_npcs ?? []
            setPlot({
                ...plot,
                plotCharacters: {
                    ...plot.plotCharacters,
                    additional_npcs: [...existing, data]
                }
            })
            setNewNPCName('')
            setNewNPCPersonality('')
            setNewNPCHistory('')
            setCreateNPCModalOpen(false)
            setNPCModalOpen(false)
        })
    }

    async function createAndSetHook() {
        if (!newHookName.trim()) return
        const npc = {
            name: newHookName,
            type: 'NPC',
            extended: {personality: newHookPersonality, history: newHookHistory}
        }
        await axios.post('/plot/npcs/', npc).then(({data}) => {
            //setNPCs(prev => [...prev, data])
            setPlot({...plot, hook: data})
            setNewHookName('')
            setNewHookPersonality('')
            setNewHookHistory('')
            setCreateHookModalOpen(false)
            setHookModalOpen(false)
        })
    }

    function addAdditionalNPCToPlot(id: number) {
        const addNPC = npcs.find(npc => npc.id === id) as NPC
        const existing = plot.plotCharacters?.additional_npcs ?? []
        setPlot({
            ...plot,
            plotCharacters: {
                ...plot.plotCharacters,
                additional_npcs: [...existing, addNPC]
            }
        })
    }

    async function openAssignedMonsterModal() {
        if (allMonsters.length === 0) {
            const {data} = await axios.get('/monsters/monsters')
            setAllMonsters(data)
        }
        setAssignedMonsterError(null)
        setSelectedAssignedMonster(null)
        setAssignedMonsterQty(1)
        setAssignedMonsterModalOpen(true)
    }

    async function addAssignedMonsterToPlot() {
        if (!selectedAssignedMonster?.id) return
        setAssignedMonsterError(null)
        const qty = selectedAssignedMonster.unique ? 1 : assignedMonsterQty
        try {
            const {data} = await axios.post(`/plot/plots/${plot.id}/assigned-monsters`, {
                monsterId: selectedAssignedMonster.id,
                quantity: qty
            })
            setPlot({
                ...plot,
                plotCharacters: {
                    ...plot.plotCharacters,
                    namedMonstersRequested: [...(plot.plotCharacters?.namedMonstersRequested ?? []), data]
                }
            })
            setAssignedMonsterModalOpen(false)
        } catch (err: any) {
            if (err?.response?.status === 409) {
                setAssignedMonsterError(err.response.data?.error ?? 'This monster is already assigned.')
            } else {
                throw err
            }
        }
    }

    async function removeAssignedMonsterFromPlot(id: number) {
        await axios.delete(`/plot/assigned-monsters/${id}`)
        setPlot({
            ...plot,
            plotCharacters: {
                ...plot.plotCharacters,
                namedMonstersRequested: (plot.plotCharacters?.namedMonstersRequested ?? []).filter(m => m.id !== id)
            }
        })
    }

    async function updateAssignedQty(id: number, delta: number) {
        const entry = (plot.plotCharacters?.namedMonstersRequested ?? []).find(m => m.id === id)
        if (!entry) return
        const newQty = Math.max(1, entry.quantity + delta)
        if (newQty === entry.quantity) return
        const {data} = await axios.patch(`/plot/assigned-monsters/${id}`, {quantity: newQty})
        setPlot({
            ...plot,
            plotCharacters: {
                ...plot.plotCharacters,
                namedMonstersRequested: (plot.plotCharacters?.namedMonstersRequested ?? []).map(m =>
                    m.id === id ? {...m, quantity: data.quantity} : m
                )
            }
        })
    }

    const removeAdditionalNPCFromPlot = (npcToRemove: NPC) => {
        const updatedNpcs = plot.plotCharacters?.additional_npcs?.filter(npc => npc.id !== npcToRemove.id) ?? []
        setPlot({
            ...plot,
            plotCharacters: {
                ...plot.plotCharacters,
                additional_npcs: updatedNpcs
            }
        })
    }

    function printPlot() {
        const plotUrl = window.location.origin + '/plot/' + plot.id;
        const printedAt = new Date().toLocaleString();
        const win = window.open('', '_blank', 'width=900,height=1100');
        if (!win) return;
        const html = document.createElement('html');
        const head = document.createElement('head');
        head.innerHTML = `<style>
            @page {
                size: 8.5in 11in;
                margin: 0.5in;
                @bottom-left {
                    content: "Printed: ${printedAt}";
                    font-family: Arial, sans-serif;
                    font-size: 9pt;
                    color: #666;
                }
            }
            body { margin: 0; padding: 0; background: white; }
            * { box-sizing: border-box; }
        </style>`;
        const body = document.createElement('body');
        const root = document.createElement('div');
        body.appendChild(root);
        html.appendChild(head);
        html.appendChild(body);
        win.document.replaceChild(html, win.document.documentElement);

        import('react-dom/client').then(({createRoot}) => {
            const reactRoot = createRoot(root);
            reactRoot.render(<PlotPrint plot={plot} plotUrl={plotUrl}/>);
            setTimeout(() => {
                win.focus();
                win.print();
            }, 800);
        });
    }

    return <React.Fragment>
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align={"center"} fontSize={24}>{plot.name}</Typography>

                    <Container sx={{textAlign: 'center'}}><ButtonGroup variant="contained"
                                                                       aria-label="Basic button group"
                                                                       size="small"><Button
                        title="Edit"
                        disabled={['SCHEDULE', 'RUN', 'COMPLETE'].includes(plot.status)}
                        onClick={toggleEdit}><EditIcon/></Button><Button title="Save" disabled={!editable}
                                                                         onClick={savePlot}><SaveIcon/></Button>
                        <Button title="Submit for Review"
                                disabled={plot.status !== 'OPEN' && plot.status !== 'REOPENED'}
                                onClick={submitForReview}><VerifiedIcon/></Button>
                        <Button title="Accept" disabled={plot.status !== 'REVIEW' || !canReview}
                                color="success"
                                onClick={acceptPlot}><CheckCircleIcon/></Button>
                        <Button title="Reject" disabled={plot.status !== 'REVIEW' || !canReview}
                                color="error"
                                onClick={rejectPlot}><CancelIcon/></Button>
                        <Button title="Reopen" disabled={!['SCHEDULE', 'RUN'].includes(plot.status) || !canReview}
                                color="warning"
                                onClick={reopenPlot}><LockOpenIcon/></Button>
                        <Button title="Run to Event" disabled={plot.status !== 'SCHEDULE' || !canReview}
                                color="success"
                                onClick={() => setEventModalOpen(true)}><ScheduleIcon/></Button>
                        <Button title="Complete"
                                disabled={plot.status !== 'RUN' || !plot.story_who?.trim() || !plot.story_outcome?.trim() || !plot.story_followup?.trim()}
                                color="success"
                                onClick={completePlot}><TaskAltIcon/></Button>
                        <Button title="Delete Plot" disabled={!['NEW', 'OPEN', 'REOPENED'].includes(plot.status)}
                                color="error"
                                onClick={() => setDeleteConfirmOpen(true)}><DeleteIcon/></Button>
                        <Button title="Print Plot"
                                disabled={!['SCHEDULE', 'RUN'].includes(plot.status)}
                                onClick={printPlot}><PrintIcon/></Button>
                    </ButtonGroup></Container>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align={"center"} variant="h6"></Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}> <TextField fullWidth id='plotstate' label='State' disabled
                                                       value={plot.status}
                        />
                        </Grid>
                        <Grid item xs={12}> <TextField fullWidth id='plotLastUpdate' label='Last Updated' disabled
                                                       value={plot.updated_at}
                        />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                freeSolo
                                disabled={!editable}
                                options={writers}
                                defaultValue={plot.writer ?? ''}
                                onInputChange={(_, value) => {
                                    plot.writer = value
                                }}
                                renderInput={(params) => <TextField {...params} fullWidth label="Writer"/>}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                freeSolo
                                disabled={!editable}
                                options={leads}
                                defaultValue={plot.lead ?? ''}
                                onInputChange={(_, value) => {
                                    plot.lead = value
                                }}
                                renderInput={(params) => <TextField {...params} fullWidth label="Team Lead"/>}
                            />
                        </Grid>
                        {plot.event && <>
                            <Grid item xs={12}>
                                <TextField fullWidth id='plotEventName' label='Scheduled Event' disabled
                                           value={plot.event.name}/>
                            </Grid>
                        </>}
                        <Grid item xs={12}>
                            <TextField fullWidth id='plotPeopleNeeded' label='People Needed' disabled
                                       value={peopleNeeded()}/>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align={"center"} variant="h6"></Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={plotTypes}
                                value={plot.type ?? null}
                                disabled={!editable}
                                freeSolo
                                onChange={(_, value) => {
                                    plot.type = value ?? ''
                                }}
                                onInputChange={(_, value) => {
                                    plot.type = value
                                }}
                                renderInput={(params) => <TextField {...params} fullWidth label="Type"/>}
                            />
                        </Grid>
                        <Grid item xs={12}> <TextField fullWidth id='plotRun' label='Run' disabled={!editable}
                                                       defaultValue={plot.run}
                                                       onChange={(ev) => {
                                                           plot.run = ev.target.value
                                                       }}/>
                        </Grid>
                        <Grid item xs={12}> <TextField fullWidth id='plotRunInfo' label='Run Info' disabled={!editable}
                                                       multiline
                                                       defaultValue={plot.info}
                                                       onChange={(ev) => {
                                                           plot.info = ev.target.value
                                                       }}/>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth disabled={plot.status === 'COMPLETE'}>
                                <InputLabel id='plotDurationLabel'>Estimated Duration</InputLabel>
                                <Select labelId='plotDurationLabel' label='Estimated Duration'
                                        value={plot.estimated_duration ?? ''}
                                        onChange={async (ev) => {
                                            const updated = {
                                                ...plot,
                                                estimated_duration: ev.target.value ? parseInt(ev.target.value as string) : null
                                            }
                                            setPlot(updated)
                                            await axios.put(`/plot/plots/${plot.id}`, updated).then(({data}) => setPlot(data))
                                        }}>
                                    <MenuItem value=""><em>Not Set</em></MenuItem>
                                    {Array.from({length: 13}, (_, i) => 60 + i * 15).map(min => {
                                        const h = Math.floor(min / 60)
                                        const m = (min % 60).toString().padStart(2, '0')
                                        return <MenuItem key={min} value={min}>{h}h {m}m</MenuItem>
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>Tags</Typography>
                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: editable ? 1 : 0}}>
                                {tags.length === 0 && !editable && (
                                    <Typography variant="body2" color="text.secondary">No tags.</Typography>
                                )}
                                {tags.map(tag => (
                                    <Chip
                                        key={tag.id}
                                        label={tag.name}
                                        size="small"
                                        onDelete={editable ? () => removeTag(tag.id) : undefined}
                                    />
                                ))}
                            </Box>
                            {editable && (
                                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                                    <Autocomplete
                                        freeSolo
                                        size="small"
                                        options={allTagNames.filter(n => !tags.some(t => t.name.toLowerCase() === n.toLowerCase()))}
                                        inputValue={newTagName}
                                        onInputChange={(_, value) => setNewTagName(value)}
                                        onChange={(_, value) => {
                                            if (value) {
                                                setNewTagName(value as string)
                                            }
                                        }}
                                        sx={{flex: 1}}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="New tag"
                                                onKeyDown={ev => {
                                                    if (ev.key === 'Enter') addTag()
                                                }}
                                            />
                                        )}
                                    />
                                    <Button variant="outlined" size="small"
                                            disabled={!newTagName.trim() || tags.some(t => t.name.toLowerCase() === newTagName.trim().toLowerCase())}
                                            onClick={addTag}>
                                        Add
                                    </Button>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align={"center"} variant="h6">Setup</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}> <TextField fullWidth id='plotSetup' label='Setup' disabled={!editable}
                                                       multiline
                                                       minRows={4}
                                                       defaultValue={plot.setup}
                                                       onChange={(ev) => {
                                                           plot.setup = ev.target.value
                                                       }}/>
                        </Grid>
                        <Grid item xs={12}> <TextField fullWidth id='plotSetupItems' label='Plot Specific Items'
                                                       disabled={!editable}
                                                       multiline
                                                       minRows={4}
                                                       defaultValue={plot.setup_items}
                                                       onChange={(ev) => {
                                                           plot.setup_items = ev.target.value
                                                       }}/>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align={"center"} variant="h6">Locations</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}> <TextField fullWidth id='plotActionLocation' label='Location'
                                                       disabled={!editable}
                                                       defaultValue={plot.action_location}
                                                       onChange={(ev) => {
                                                           plot.action_location = ev.target.value
                                                       }}/>
                        </Grid>
                        <Grid item xs={12}> <TextField fullWidth id='plotHookLocation' label='Hook Location'
                                                       disabled={!editable}
                                                       defaultValue={plot.hook_location}
                                                       onChange={(ev) => {
                                                           plot.hook_location = ev.target.value
                                                       }}/>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align={"center"} variant="h6">Why?</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}> <TextField fullWidth id='plotShortDesc' label='Short Description'
                                                       disabled={!editable}
                                                       defaultValue={plot.short_desc}
                                                       onChange={(ev) => {
                                                           plot.short_desc = ev.target.value
                                                       }}/>
                        </Grid>
                        <Grid item xs={12}> <TextField fullWidth id='plotDescription' label='Description'
                                                       disabled={!editable}
                                                       multiline
                                                       minRows={8}
                                                       defaultValue={plot.description}
                                                       onChange={(ev) => {
                                                           plot.description = ev.target.value
                                                       }}/>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>


            <Grid item xs={12} sm={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align={"center"} variant="h6">Hook
                        <Tooltip title="Set Hook NPC"><span><Button disabled={!editable}
                                                                    onClick={(ev) => {
                                                                        setHookModalOpen(true)
                                                                    }}
                        ><PersonAddIcon/></Button></span></Tooltip></Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}> <TextField fullWidth id='plotHookName' label='Name' disabled
                                                      value={plot.hook?.name ?? ""}
                                                      onChange={(e) => {
                                                      }}
                        />
                        </Grid>
                        <Grid item xs={6}> <TextField fullWidth id='plotHookPersonality' label='Personality'
                                                      disabled
                                                      multiline
                                                      minRows={3}
                                                      value={plot.hook?.extended?.personality ?? ""}
                                                      onChange={(e) => {
                                                      }}
                        />
                        </Grid>
                        <Grid item xs={12}> <TextField fullWidth id='plotHookHistory' label='History'
                                                       disabled
                                                       multiline
                                                       minRows={6}
                                                       value={plot.hook?.extended?.history ?? ""}
                                                       onChange={(e) => {
                                                       }}
                        />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align={"center"} variant="h6">Additional NPCs
                        <Tooltip title="Add NPC"><span><Button disabled={!editable}
                                                               onClick={(ev) => {
                                                                   setNPCModalOpen(true)
                                                               }}><PersonAddIcon/></Button></span></Tooltip></Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}/>
                        {plot.plotCharacters?.additional_npcs?.map((npc) => {
                            return <Grid key={npc.id} item md={12} sm={12} xs={12}>
                                <Link to={`/npc/${npc.id}`}>{npc.name}</Link> <Tooltip title="Remove NPC"><span><Button
                                disabled={!editable}
                                onClick={() => removeAdditionalNPCFromPlot(npc)}><PersonRemoveIcon/></Button></span></Tooltip>
                            </Grid>
                        })}
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align={"center"} variant="h6">Monsters
                        <Tooltip title="Add Monster Type"><span><Button disabled={!editable}
                                                                        onClick={handleOpenMonsterModal}><PersonAddIcon/></Button></span></Tooltip></Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}/>
                        {plot.plotCharacters?.monsters_requested?.map((entry) => (
                            <Grid key={entry.type.id} item xs={12}
                                  sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Typography sx={{flex: 1}}>
                                    {userRoles.includes('MonsterManager')
                                        ? <Link
                                            to={`/monsters/type/${entry.type.id}`}>{entry.type.species?.name} — {entry.type.name}</Link>
                                        : <>{entry.type.species?.name} — {entry.type.name}</>
                                    }
                                </Typography>
                                <Tooltip title="Decrease Quantity"><span><Button size="small" disabled={!editable}
                                                                                 onClick={() => updateMonsterQty(entry.type.id as number, -1)}>
                                    <RemoveIcon fontSize="small"/>
                                </Button></span></Tooltip>
                                <Typography>{entry.quantity}</Typography>
                                <Tooltip title="Increase Quantity"><span><Button size="small" disabled={!editable}
                                                                                 onClick={() => updateMonsterQty(entry.type.id as number, 1)}>
                                    <AddIcon fontSize="small"/>
                                </Button></span></Tooltip>
                                <Tooltip title="Remove Monster"><span><Button size="small" disabled={!editable}
                                                                              onClick={() => removeMonsterFromPlot(entry.type.id as number)}>
                                    <PersonRemoveIcon fontSize="small"/>
                                </Button></span></Tooltip>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Grid>
            {(userRoles.includes('MonsterManager') || userRoles.includes('Loremaster')) && (
                <Grid item xs={12} sm={6}>
                    <Paper elevation={2} sx={{p: 1}}>
                        <Typography align={"center"} variant="h6">Assigned Monsters
                            {userRoles.includes('MonsterManager') && (
                                <Tooltip title="Assign Monster"><span><Button
                                    onClick={openAssignedMonsterModal}><PersonAddIcon/></Button></span></Tooltip>
                            )}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}/>
                            {(plot.plotCharacters?.namedMonstersRequested ?? []).length === 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">No monsters
                                        assigned.</Typography>
                                </Grid>
                            )}
                            {(plot.plotCharacters?.namedMonstersRequested ?? []).map((entry) => (
                                <Grid key={entry.id} item xs={12} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <Typography sx={{flex: 1}}>
                                        <Link to={`/monsters/${entry.monster.id}`}>{entry.monster.name}</Link>
                                        {entry.monster.unique &&
                                            <Chip size="small" label="Unique" color="warning" sx={{ml: 1}}/>}
                                        {(entry.monster as any).dedicated_marshal &&
                                            <Chip size="small" label="Ded. Marshal" color="secondary" sx={{ml: 1}}/>}
                                    </Typography>
                                    {userRoles.includes('MonsterManager') ? (<>
                                        <Tooltip title="Decrease Quantity"><span><Button size="small"
                                                                                         onClick={() => updateAssignedQty(entry.id, -1)}><RemoveIcon
                                            fontSize="small"/></Button></span></Tooltip>
                                        <Typography>{entry.quantity}</Typography>
                                        <Tooltip title="Increase Quantity"><span><Button size="small"
                                                                                         onClick={() => updateAssignedQty(entry.id, 1)}><AddIcon
                                            fontSize="small"/></Button></span></Tooltip>
                                        <Tooltip title="Remove Assignment"><span><Button size="small"
                                                                                         onClick={() => removeAssignedMonsterFromPlot(entry.id)}><PersonRemoveIcon
                                            fontSize="small"/></Button></span></Tooltip>
                                    </>) : (
                                        <Typography>{entry.quantity}</Typography>
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Grid>
            )}
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align={"center"} variant="h6">Story Notes</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}> <TextField fullWidth id='plotNotesWho' label='Who Was Involved?'
                                                       disabled={plot.status !== 'RUN'}
                                                       value={plot.story_who ?? ''}
                                                       onChange={ev => setPlot({...plot, story_who: ev.target.value})}
                        />
                        </Grid>
                        <Grid item xs={12}> <TextField fullWidth id='plotNotesOutcome' label='What was the Outcome?'
                                                       disabled={plot.status !== 'RUN'}
                                                       multiline
                                                       minRows={4}
                                                       value={plot.story_outcome ?? ''}
                                                       onChange={ev => setPlot({
                                                           ...plot,
                                                           story_outcome: ev.target.value
                                                       })}
                        />
                        </Grid>
                        <Grid item xs={12}> <TextField fullWidth id='plotNotesFollowup'
                                                       label='What follow up was asked for by the players?'
                                                       disabled={plot.status !== 'RUN'}
                                                       multiline
                                                       minRows={4}
                                                       value={plot.story_followup ?? ''}
                                                       onChange={ev => setPlot({
                                                           ...plot,
                                                           story_followup: ev.target.value
                                                       })}
                        />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>

        <Modal
            open={hookModalOpen}
            onClose={handleCloseHookModal}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={modalStyle}>
                <Typography id="modal-modal-title" variant="h6" component="h2" gutterBottom>
                    Please Select an NPC to have Hook this module.
                </Typography>
                <Autocomplete
                    disablePortal
                    options={nPCAutoComplete()}
                    sx={{width: 300}}
                    renderInput={(params) => <TextField {...params} label="NPC"/>}
                    onChange={(_, value) => {
                        if (value) {
                            addHookToPlot(value.id)
                            handleCloseHookModal()
                            setPlot({...plot})
                        }
                    }}
                />
                <Container sx={{textAlign: 'center', mt: 2}}>
                    <Button variant="outlined" startIcon={<PersonAddIcon/>}
                            onClick={() => setCreateHookModalOpen(true)}>
                        Create New NPC
                    </Button>
                </Container>
            </Box>
        </Modal>

        <Modal
            open={createHookModalOpen}
            onClose={() => setCreateHookModalOpen(false)}
            aria-labelledby="create-hook-modal-title"
        >
            <Box sx={modalStyle}>
                <Typography id="create-hook-modal-title" variant="h6" component="h2" gutterBottom>
                    Create New Hook NPC
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Name" required
                                   value={newHookName}
                                   onChange={(ev) => setNewHookName(ev.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Personality" multiline rows={3}
                                   value={newHookPersonality}
                                   onChange={(ev) => setNewHookPersonality(ev.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="History" multiline rows={4}
                                   value={newHookHistory}
                                   onChange={(ev) => setNewHookHistory(ev.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{textAlign: 'center'}}>
                        <Button variant="contained" disabled={!newHookName.trim()}
                                onClick={createAndSetHook}>
                            Save & Set as Hook
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Modal>


        <Modal
            open={npcModalOpen}
            onClose={handleCloseNPCModal}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={modalStyle}>
                <Typography id="modal-modal-title" variant="h6" component="h2" gutterBottom>
                    Please Select an NPC to Add.
                </Typography>
                <Autocomplete
                    disablePortal
                    options={nPCAutoComplete()}
                    sx={{width: 300}}
                    renderInput={(params) => <TextField {...params} label="NPC"/>}
                    onChange={(_, value) => {
                        if (value) {
                            addAdditionalNPCToPlot(value.id)
                            handleCloseNPCModal()
                        }
                    }}
                />
                <Container sx={{textAlign: 'center', mt: 2}}>
                    <Button variant="outlined" startIcon={<PersonAddIcon/>}
                            onClick={() => setCreateNPCModalOpen(true)}>
                        Create New NPC
                    </Button>
                </Container>
            </Box>
        </Modal>

        <Modal
            open={createNPCModalOpen}
            onClose={() => setCreateNPCModalOpen(false)}
            aria-labelledby="create-npc-modal-title"
        >
            <Box sx={modalStyle}>
                <Typography id="create-npc-modal-title" variant="h6" component="h2" gutterBottom>
                    Create New NPC
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Name" required
                                   value={newNPCName}
                                   onChange={(ev) => setNewNPCName(ev.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Personality" multiline rows={3}
                                   value={newNPCPersonality}
                                   onChange={(ev) => setNewNPCPersonality(ev.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="History" multiline rows={4}
                                   value={newNPCHistory}
                                   onChange={(ev) => setNewNPCHistory(ev.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{textAlign: 'center'}}>
                        <Button variant="contained" disabled={!newNPCName.trim()}
                                onClick={createAndAddNPC}>
                            Save & Add to Plot
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Modal>

        <Modal
            open={eventModalOpen}
            onClose={() => {
                setEventModalOpen(false);
                setSelectedEvent(null);
                setPlotStartTime('');
            }}
            aria-labelledby="event-modal-title"
        >
            <Box sx={modalStyle}>
                <Typography id="event-modal-title" variant="h6" component="h2" align="center" gutterBottom>
                    Add to Event
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                            disablePortal
                            options={events.map(e => ({
                                label: e.start ? `${e.name} (${new Date(e.start).toLocaleDateString()})` : e.name,
                                id: e.id,
                                event: e
                            }))}
                            renderInput={(params) => <TextField {...params} label="Event"/>}
                            onChange={(_, value) => {
                                setSelectedEvent(value ? value.event : null);
                                setPlotStartTime('');
                            }}
                        />
                    </Grid>
                    {selectedEvent && (
                        <Grid item xs={12}>

                            <TextField
                                fullWidth
                                label={`Plot Start Time (optional, ${tz})`}
                                type="datetime-local"
                                value={plotStartTime}
                                InputLabelProps={{shrink: true}}
                                inputProps={{
                                    min: utcToLocalInput(selectedEvent.start, tz),
                                    max: utcToLocalInput(selectedEvent.end, tz),
                                }}
                                onChange={ev => setPlotStartTime(ev.target.value)}
                            />
                        </Grid>
                    )}
                </Grid>
                <Container sx={{textAlign: 'center', mt: 2}}>
                    <Button variant="contained" disabled={!selectedEvent} onClick={assignToEvent}>
                        Add to Event
                    </Button>
                </Container>
            </Box>
        </Modal>

        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
            <DialogTitle>Delete Plot</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to delete "{plot.name}"? This action cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                <Button color="error" onClick={confirmDelete}>Delete</Button>
            </DialogActions>
        </Dialog>

        <Modal
            open={assignedMonsterModalOpen}
            onClose={() => setAssignedMonsterModalOpen(false)}
            aria-labelledby="assign-monster-modal-title"
        >
            <Box sx={modalStyle}>
                <Typography id="assign-monster-modal-title" variant="h6" component="h2" align="center">
                    Assign Monster
                </Typography>
                <Grid container spacing={2} sx={{mt: 1}}>
                    <Grid item xs={12}>
                        <Autocomplete
                            disablePortal
                            options={allMonsters
                                .filter(m => !(plot.plotCharacters?.namedMonstersRequested ?? []).some(a => a.monster.id === m.id))
                                .map(m => ({
                                    label: `${m.name}${m.unique ? ' (Unique)' : ''}`,
                                    id: m.id as number,
                                    monster: m
                                }))}
                            renderInput={(params) => <TextField {...params} label="Monster"/>}
                            onChange={(_, value) => setSelectedAssignedMonster(value ? value.monster : null)}
                        />
                    </Grid>
                    {!selectedAssignedMonster?.unique && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Quantity"
                                type="number"
                                inputProps={{min: 1}}
                                value={assignedMonsterQty}
                                onChange={(ev) => setAssignedMonsterQty(Math.max(1, parseInt(ev.target.value) || 1))}
                            />
                        </Grid>
                    )}
                    {assignedMonsterError && (
                        <Grid item xs={12}>
                            <Typography color="error" variant="body2">{assignedMonsterError}</Typography>
                        </Grid>
                    )}
                </Grid>
                <Container sx={{textAlign: "center", mt: 2}}>
                    <Button variant="contained" disabled={!selectedAssignedMonster} onClick={addAssignedMonsterToPlot}>
                        Assign
                    </Button>
                </Container>
            </Box>
        </Modal>

        <Modal
            open={monsterModalOpen}
            onClose={handleCloseMonsterModal}
            aria-labelledby="monster-modal-title"
        >
            <Box sx={modalStyle}>
                <Typography id="monster-modal-title" variant="h6" component="h2" align="center">
                    Add Monster Type
                </Typography>
                <Grid container spacing={2} sx={{mt: 1}}>
                    <Grid item xs={12}>
                        <Autocomplete
                            disablePortal
                            options={monsterTypeOptions()}
                            renderInput={(params) => <TextField {...params} label="Monster Type"/>}
                            onChange={(_, value) => {
                                setSelectedMonsterType(value ? value.type : null)
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Quantity"
                            type="number"
                            inputProps={{min: 1}}
                            value={monsterQty}
                            onChange={(ev) => setMonsterQty(Math.max(1, parseInt(ev.target.value) || 1))}
                        />
                    </Grid>
                </Grid>
                <Container sx={{textAlign: "center", mt: 2}}>
                    <Button variant="contained" disabled={!selectedMonsterType} onClick={addMonsterToPlot}>
                        Add
                    </Button>
                </Container>
            </Box>
        </Modal>
    </React.Fragment>
}
